// httpservice.ts ↔ the client's HTTP seam: game/httpservice.h (HttpService /
// HttpResponseHandler), game/httprequest.{h,cpp} (HttpRequest), game/map.cpp
// (Map — the headers map slice the DLC stack reads), game/progresscallback.h
// (ProgressCallback), game/serviceurls.{h,cpp} (the production content URLs)
// and game/sdl/web/webhttpservice.cpp + webhttprequest.cpp — the WEB build's
// service implementation (emscripten_async_wget2_data), here FetchHttpService
// over an injected fetch.
//
// SHAPE NOTE (the one C++→TS seam): the C++ HttpService is already an injected
// interface (gphttp); callbacks arrive on the game thread via the message
// queue. Here they arrive on the JS event loop (FetchHttpService) or wherever
// a scripted test service fires them — the consumers (httpindexloader.ts,
// httppackmanager.ts, httppackinfomanager.ts) are callback-driven state
// machines either way, exactly like the C++.
//
// base/format.cpp rides along: the DLC stack's only reaches into base::Format
// are the hex/integer helpers (ToHex/FromHex/GetHexDigit/ToDword/ToInteger/
// ValidateString) — ported here verbatim. Format::ToString (vsnprintf) has no
// TS analog: every call site formats with template literals.

// ── base/format.cpp ───────────────────────────────────────────────────────────

// base/format.cpp:24-39 — lowercase hex of cb bytes ("0" when oversized)
export function ToHex(pb: Uint8Array, cb: number = pb.length): string {
  const map = '0123456789abcdef';
  if (cb >= (512 - 1) / 2) return '0';
  let sz = '';
  for (let i = 0; i < cb; i++) {
    sz += map[(pb[i] >> 4) & 0xf];
    sz += map[pb[i] & 0xf];
  }
  return sz;
}

// base/format.cpp:41-53 — the *pb out param becomes the return (null = failure)
export function GetHexDigit(ch: string): number | null {
  if (ch >= '0' && ch <= '9') return ch.charCodeAt(0) - 0x30;
  if (ch >= 'A' && ch <= 'F') return ch.charCodeAt(0) - 0x41 + 10;
  if (ch >= 'a' && ch <= 'f') return ch.charCodeAt(0) - 0x61 + 10;
  return null;
}

// base/format.cpp:55-70 — fills pb until the string ends or cb bytes are done
// (a short string "succeeds" with the tail unfilled — preserved); an odd
// trailing digit fails (GetHexDigit of the NUL terminator).
export function FromHex(psz: string, pb: Uint8Array, cb: number): boolean {
  let ich = 0;
  let ib = 0;
  while (ich < psz.length && cb !== 0) {
    const hi = GetHexDigit(psz[ich++] ?? '\0');
    if (hi === null) return false;
    const lo = GetHexDigit(psz[ich++] ?? '\0');
    if (lo === null) return false;
    pb[ib++] = (hi << 4) | lo;
    cb--;
  }
  return true;
}

// base/format.cpp:94-122 — base 10: digits with an optional leading '-';
// base 16: hex digits only
export function ValidateString(psz: string, nBase: number): boolean {
  if (nBase === 10) {
    for (let i = 0; i < psz.length; i++) {
      const ch = psz[i];
      if (ch < '0' || ch > '9') {
        if (i !== 0 || ch !== '-') return false;
      }
    }
    return true;
  }
  if (nBase === 16) {
    for (let i = 0; i < psz.length; i++) {
      const ch = psz[i];
      if (!((ch >= '0' && ch <= '9') || (ch >= 'a' && ch <= 'f') || (ch >= 'A' && ch <= 'F'))) {
        return false;
      }
    }
    return true;
  }
  return false;
}

// base/format.cpp:72-81 — strtoll + (dword) cast; the out param becomes the
// return (null = failure)
export function ToDword(psz: string, nBase: number): number | null {
  if (!ValidateString(psz, nBase)) return null;
  return Math.trunc(parseInt(psz, nBase)) >>> 0;
}

// base/format.cpp:83-92 — strtol + (int) cast
export function ToInteger(psz: string, nBase: number): number | null {
  if (!ValidateString(psz, nBase)) return null;
  const l = parseInt(psz, nBase);
  return Number.isNaN(l) ? 0 : Math.trunc(l) | 0;
}

// ── game/map.cpp — Map (the case-sensitive string→string headers map) ─────────

export class HeadersMap {
  private m = new Map<string, string>();

  // game/map.cpp:17 — Map::SetValue
  SetValue(pszKey: string, pszValue: string): void {
    this.m.set(pszKey, pszValue);
  }

  // game/map.cpp:5 — Map::GetValue (the char-buffer out param collapses to the
  // return; null = not found)
  GetValue(pszKey: string): string | null {
    return this.m.get(pszKey) ?? null;
  }

  // game/map.cpp:29 — Map::EnumKeys (the Enum cursor collapses to an array)
  EnumKeys(): string[] {
    return [...this.m.keys()];
  }
}

// ── game/httpservice.h — the handler + service interfaces ─────────────────────

export interface HttpResponseHandler {
  /** OnReceivedResponse(preq, code, pheaders) — pheaders may be null (the web
   *  error arm posts (400, NULL), webhttprequest.cpp:52). */
  OnReceivedResponse(preq: HttpRequest, code: number, pheaders: HeadersMap | null): void;
  /** OnReceivedData(preq, pbb) — base::ByteBuffer ⇒ a bytes chunk. */
  OnReceivedData(preq: HttpRequest, pbb: Uint8Array): void;
  OnFinishedLoading(preq: HttpRequest): void;
  OnError(preq: HttpRequest, pszError: string): void;
}

export interface HttpService {
  NewRequest(phandler: HttpResponseHandler): HttpRequest | null;
  SubmitRequest(preq: HttpRequest): void;
  ReleaseRequest(preq: HttpRequest): void;
}

// game/progresscallback.h — the DLC download progress callbacks
export interface ProgressCallback {
  OnBegin(ctx: unknown, cbLength: number): void;
  OnProgress(ctx: unknown, cbTotal: number, cbLength: number): void;
  OnFinished(ctx: unknown): void;
  OnError(ctx: unknown, pszError: string): void;
}

// ── game/httprequest.{h,cpp} — HttpRequest ────────────────────────────────────

export class HttpRequest {
  // game/httprequest.cpp:5-9 (url_/method_/pbb_/timeout_ init) — the TS request
  // also carries its handler (the C++ subclasses do: webhttprequest.h)
  protected url_ = '';
  protected method_ = 'GET';
  protected pbb_: Uint8Array | null = null;
  protected timeout_ = 0;
  protected headers_ = new HeadersMap();
  readonly handler: HttpResponseHandler;

  constructor(phandler: HttpResponseHandler) {
    this.handler = phandler;
  }

  // game/httprequest.cpp:15-17
  SetURL(pszUrl: string): void {
    this.url_ = pszUrl;
  }
  // game/httprequest.cpp:19-21 (the char-buffer out param collapses)
  GetURL(): string {
    return this.url_;
  }
  // game/httprequest.cpp:23-25
  SetMethod(pszMethod: string): void {
    this.method_ = pszMethod;
  }
  // game/httprequest.cpp:27-29
  GetMethod(): string {
    return this.method_;
  }
  // game/httprequest.cpp:31-34
  SetBody(pbb: Uint8Array | null): void {
    this.pbb_ = pbb;
  }
  // game/httprequest.cpp:36-38
  GetBody(): Uint8Array | null {
    return this.pbb_;
  }
  // game/httprequest.cpp:40-42
  SetTimeout(cSeconds: number): void {
    this.timeout_ = cSeconds;
  }
  // game/httprequest.cpp:44-46
  GetTimeout(): number {
    return this.timeout_;
  }
  // game/httprequest.cpp:48-50
  SetHeaders(pheaders: HeadersMap): void {
    this.headers_ = pheaders;
  }
  // game/httprequest.cpp:52-54
  GetHeaders(): HeadersMap {
    return this.headers_;
  }
}

// ── game/sdl/web/webhttpservice.cpp + webhttprequest.cpp — FetchHttpService ───
//
// The WEB build's semantics, kept exactly: the whole body arrives in ONE
// OnReceivedData after an OnReceivedResponse(200, {Content-Length}) and before
// OnFinishedLoading (webhttprequest.cpp OnLoad); failures post
// OnReceivedResponse(400, NULL) then OnError (webhttprequest.cpp OnError —
// the 400 is hardcoded in the C++ too). wget2's OnProgress was a Trace-only
// TODO in the shipped client — no incremental delivery there either.

/** The injected fetch (production: globalThis.fetch; tests script it).
 *  headers/body carry the POST arm (the sync-error upload): the shipped
 *  client's wget2 sends the request param string as the body with
 *  Content-type: application/x-www-form-urlencoded (the emscripten
 *  _emscripten_async_wget2_data POST arm, game/web_rel index.js). */
export type FetchLike = (
  url: string,
  init: {
    method: string;
    headers?: Record<string, string>;
    body?: Uint8Array;
  },
) => Promise<{
  status: number;
  statusText: string;
  arrayBuffer(): Promise<ArrayBuffer>;
}>;

export class FetchHttpService implements HttpService {
  private readonly fetchFn: FetchLike;
  // ReleaseRequest must stop late callbacks: the C++ web Release() is a no-op
  // Trace (the SDL/curl arm cancels) — the loaders guard with code_/error_
  // state, but the GC port drops released requests outright (the safe superset)
  private readonly released = new WeakSet<HttpRequest>();

  constructor(fetchFn?: FetchLike) {
    // wrap rather than alias the global: `this.fetchFn(...)` would invoke
    // window.fetch with `this` = the service — an Illegal invocation in
    // browsers (Node's fetch doesn't care, so only the browser host saw it)
    this.fetchFn =
      fetchFn ?? ((url, init) => (globalThis.fetch as unknown as FetchLike)(url, init));
  }

  // game/sdl/web/webhttpservice.cpp:6-8
  NewRequest(phandler: HttpResponseHandler): HttpRequest | null {
    return new HttpRequest(phandler);
  }

  // game/sdl/web/webhttpservice.cpp:10-13 — preqT->Submit()
  SubmitRequest(preq: HttpRequest): void {
    void this.Submit(preq);
  }

  // game/sdl/web/webhttpservice.cpp:15-18 — preqT->Release()
  ReleaseRequest(preq: HttpRequest): void {
    this.released.add(preq);
  }

  // webhttprequest.cpp:16-23 — Submit: emscripten_async_wget2_data ⇒ fetch;
  // the OnLoad/OnError callbacks below fire on the JS event loop (the C++
  // equivalents post to the game thread's message queue)
  private async Submit(preq: HttpRequest): Promise<void> {
    try {
      // the wget2 POST arm (the shipped client's _emscripten_async_wget2_data):
      // `http.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
      //  http.send(_param);` — _param is the request body as a C STRING, so the
      // ByteBuffer's trailing NUL (statetracker.cpp:123-125 "Zero terminate")
      // never reaches the wire. GET sends null with no extra header.
      const init: Parameters<FetchLike>[1] = { method: preq.GetMethod() };
      if (preq.GetMethod() === 'POST') {
        init.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        init.body = preq.GetBody() ?? new Uint8Array(0);
      }
      const res = await this.fetchFn(preq.GetURL(), init);
      if (res.status >= 200 && res.status < 300) {
        const ab = await res.arrayBuffer();
        if (this.released.has(preq)) return;
        this.OnLoad(preq, new Uint8Array(ab));
      } else {
        // wget2 reports HTTP failures through onerror with the status text
        if (this.released.has(preq)) return;
        this.OnError(preq, res.statusText !== '' ? res.statusText : String(res.status));
      }
    } catch (e) {
      if (this.released.has(preq)) return;
      this.OnError(preq, e instanceof Error ? e.message : String(e));
    }
  }

  // webhttprequest.cpp:29-44 — OnLoad: synthesize Content-Length, then
  // response(200) → data(whole body) → finished
  private OnLoad(preq: HttpRequest, pb: Uint8Array): void {
    const headers = new HeadersMap();
    headers.SetValue('Content-Length', String(pb.length));
    preq.handler.OnReceivedResponse(preq, 200, headers);
    preq.handler.OnReceivedData(preq, pb);
    preq.handler.OnFinishedLoading(preq);
  }

  // webhttprequest.cpp:46-55 — OnError: response(400, NULL) then the error
  private OnError(preq: HttpRequest, pszStatus: string): void {
    preq.handler.OnReceivedResponse(preq, 400, null);
    preq.handler.OnError(preq, pszStatus);
  }
}

// ── game/serviceurls.cpp:17-19 (the production block) ─────────────────────────
// The Cloudflare content worker serves the same tree (workers/content/src/
// index.ts): GET /wi/index, GET /wi/pack/<id8>-<md5>, GET /wi/info/<id8>-<md5>;
// the ?c=&v= query params are ignored when locating the object.

export const kszIndexUrl = 'https://content.warfareincorporated.com/wi/index';
export const kszPackInfoUrl = 'https://content.warfareincorporated.com/wi/info';
export const kszPackUrl = 'https://content.warfareincorporated.com/wi/pack';
// serviceurls.cpp:25 — the sync-error tracker upload target (SimUI.cpp:568-572
// ReportSyncError POSTs the StateTracker ToJson here, ?gameid=&pid=). The WASM
// client posts to this exact URL. The legacy Apache box ran a real CGI there;
// today only /wi/* routes to the wi-content worker (GET/HEAD-only,
// workers/content/src/index.ts:39-41) and /cgi-bin/* falls through to the
// static Pages shell (probed 2026-06-13: GET → 200 index.html fallback,
// OPTIONS → 405; a POST body is accepted by NOBODY). It stays the faithful
// target — same wire behavior as the shipping WASM client — until a worker
// grows the endpoint. (The legacy api /api/syncerror 302 →
// www.warfareincorporated.com/cgi-bin/upload.cgi is equally dead and the
// client never called it.)
export const kszSyncErrorUploadUrl = 'https://content.warfareincorporated.com/cgi-bin/upload.cgi';

// mpshared/mpht.h:14 — kdwClientID (the ?c= query param)
export const kdwClientID = 0x00000001;
// mpshared/mpht.h:108 — knVersionSimulation (the ?v= query param). Single source of truth is the
// sim (it's a sim-behavior version); re-exported here as the network value.
export { knVersionSimulation } from '../../../sim/src/simversion.ts';
